import { useCallback, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { getLanguage, HOT_KEY, useHotKey } from '@web-chat-core/ui-kit';
import { useManageVideoDevices } from 'features/PreJoin/hooks/useManageVideoDevices';
import { useManageBackgroundProcessor } from 'features/VirtualBackground/hooks/useManageBackgroundProcessor';
import { useUserSettingsContext } from 'contexts';
import { WaitingApprovalState } from 'features/PreJoin/components/WaitingApprovalState';
import { LANGUAGE_STORAGE_KEY } from 'consts';
import { Header } from 'ui/components/Header/Header';
import { isWaitingLottieLayout } from '../../features/PreJoin/utils';
import { WaitingApprovalBody, WaitingPageContainer, WaitingPageContent } from './view';

interface WaitingApprovalPageProps {
    isJoinRejected: boolean;
    onRetry: () => void;
    onDismissRejected?: () => void;
    embeddedMode?: boolean;
}

export const WaitingApprovalPage = observer(
    ({ isJoinRejected, onRetry, onDismissRejected, embeddedMode = false }: WaitingApprovalPageProps) => {
        const { audioEnabled, setAudioEnabled } = useUserSettingsContext();
        const { toggleVideo } = useManageVideoDevices();

        useManageBackgroundProcessor();

        const lang = localStorage.getItem(LANGUAGE_STORAGE_KEY) || getLanguage();
        const [language, setLanguage] = useState(lang);

        const setPersistLanguage = useCallback((lang: string) => {
            localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
            setLanguage(lang);
        }, []);

        const toggleAudio = useCallback(() => {
            setAudioEnabled(!audioEnabled);
        }, [audioEnabled, setAudioEnabled]);

        useHotKey(HOT_KEY.TOGGLE_MICRO, toggleAudio, { handlerName: 'toggle-micro-waiting' }, [audioEnabled]);
        useHotKey(HOT_KEY.TOGGLE_CAMERA, toggleVideo, { handlerName: 'toggle-camera-waiting' }, []);

        return (
            <WaitingPageContainer id="modal-root">
                <main>
                    <WaitingPageContent>
                        {isWaitingLottieLayout && (
                            <Header
                                language={language}
                                embeddedMode={embeddedMode}
                                setPersistLanguage={setPersistLanguage}
                                isLanguage={false}
                            />
                        )}
                        <WaitingApprovalBody>
                            <WaitingApprovalState
                                isJoinRejected={isJoinRejected}
                                onRetry={onRetry}
                                onDismiss={onDismissRejected}
                            />
                        </WaitingApprovalBody>
                    </WaitingPageContent>
                </main>
            </WaitingPageContainer>
        );
    },
);
